import type { FC, ReactNode } from "react";

import type { IconName } from "@similarweb/icons";
import type { IChipItemProps, ButtonType } from "@similarweb/ui-components";

import type { ISegmentPageResultsProps } from "app/pages/segments/wizard/SegmentRulesStep/SegmentResultsSection/SegmentResultsSectionTypes";
import type {
    ISegmentRuleItem,
    IWordsFilterConfig,
    ISegmentsUrlParams,
} from "app/services/segments/segmentsWizardServiceTypes";
import type { EDeviceType } from "app/UtilitiesAndConstants/Constants/DevicesTypes";
import { RuleTypes } from "pro-features/components/RulesQueryBuilder/src/RulesQueryBuilderTypes";
import type {
    IFolderPredictions,
    IRule,
    IWordPredictions,
    IPopularSegments,
} from "pro-features/components/RulesQueryBuilder/src/RulesQueryBuilderTypes";
import type { ISite } from "pro-features/components/Workspace/Wizard/src/types";

import type {
    ISegmentUrlListItemHighlightProps,
    ISegmentUrl,
    ISegmentUrlListProps,
} from "../../wizard/SegmentRulesStep/SegmentUrlList/SegmentUrlListTypes";

export type SegmentWizardContextType = {
    requestWordPredictions(wordPredictionsLoading: boolean): void;
    receiveWordPredictions(wordPredictions: IWordPredictions): void;
    requestFolderPredictions(folderPredictionsLoading: boolean): void;
    receiveFolderPredictions(folderPredictions: IFolderPredictions): void;
    setSegmentRules(segmentRules: IRule[]): void;
    requestSegmentsPreview(segmentsPreviewLoading: boolean): void;
    resetSegmentsWizardData: () => void;
    setSegmentsReady(segmentsReady: boolean): void;
    setSelectedCountry(country: ISelectedCountry): void;
    wordPredictionsLoading: boolean;
    wordPredictions: IWordPredictions;
    segmentRules: IRule[];
    segmentsPreviewLoading: boolean;
    folderPredictions: IFolderPredictions;
    folderPredictionsLoading: boolean;
    segmentsReady: boolean;
    selectedCountry: ISelectedCountry;
};

export type ISegmentDomainSelection = {
    // Domain selection
    onSelectMainSite: (site: ISite) => void;
    onClearMainSite: () => void;
    selectedSite?: ISite;
    isSelectedSiteAllowed?: boolean;
};

export type IPagesWrapper = {
    rules: IRule[];
    title: string;
    ruleType: RuleTypes;
    isMidTierUser: boolean;
    onCloseRule: (rule: IRule, ruleIndex?: number, filterType?: string, selectedTab?: number) => void;
    domain: string;
    getUrlsPreview: (rules: IRule[]) => Promise<ISegmentUrl[]>;
    hideUrlsPreview: boolean;
    hasExistingUrlsCompoundStringNoRobots: (rules: IRule[]) => Promise<boolean>;
    updateWordPredictionsIfNeeded: (currentRuleIndex: number, ruleType: RuleTypes) => void;
    doExcludeRulesExist?: boolean;
    isMidtierUserAndOneRuleCreated: boolean;
    resetActiveIndex: () => void;
};

export type IRulePagesWrapper = Pick<
    IPagesWrapper,
    | "isMidTierUser"
    | "onCloseRule"
    | "domain"
    | "getUrlsPreview"
    | "hideUrlsPreview"
    | "hasExistingUrlsCompoundStringNoRobots"
    | "updateWordPredictionsIfNeeded"
    | "resetActiveIndex"
> & {
    segmentIncludeRules: IRule[];
    segmentExcludeRules: IRule[];
    isMidtierUserAndOneRuleCreated: boolean;
};

interface FilterObject {
    filterKey: string;
    order: number;
    component: ReactNode | FC;
    title: string;
}

export interface IRulesFilterRow extends IRulesGroup {
    filterType?: string;
    sortedFiltersArray?: FilterObject[];
    shouldDisplayRuleTitle: boolean;
    resetActiveIndex: () => void;
}

export interface IRulesGroup {
    isMidTierUser: boolean;
    onCloseRule: (rule: IRule, ruleIndex?: number, filterType?: string, selectedTab?: number) => void;
    domain: string;
    getUrlsPreview: (rules: IRule[]) => Promise<ISegmentUrl[]>;
    hideUrlsPreview: boolean;
    hasExistingUrlsCompoundStringNoRobots: (rules: IRule[]) => Promise<boolean>;
    updateWordPredictionsIfNeeded: (currentRuleIndex: number, ruleType: RuleTypes) => void;
    ruleIndex?: number;
    ruleType: RuleTypes;
    rule: IRule;
    nextRule: IRule;
    isFinalGroup?: boolean;
    displayForEmptyRule?: boolean;
    hasAtLeastOneRule: boolean;
    doExcludeRulesExist?: boolean;
    isMidtierUserAndOneRuleCreated: boolean;
    resetActiveIndex: () => void;
}

export type ISelectedDomain = {
    onClearMainSite: () => void;
    selectedSite?: ISite;
    isSelectedSiteAllowed?: boolean;
};

export type ISelectedCountry = {
    id: number;
    text: string;
};

export type ISegmentsValidationSectionProps = {
    segmentShare?: number;
    segmentVisits?: number;
};

export type ISegmentsWebSourceSectionProps = {
    webSource: EDeviceType;
};

export type ISegmentFilterListItem = {
    childKey?: string;
    tooltipKey: string;
    iconName: IconName;
    textKey: string;
    onClick: (selectedValue) => void;
    isTabsComponent?: boolean;
    isSelectedValue?: boolean;
    hideUrlsPreview?: boolean;
    filterKey?: string;
    tooltipTextI18nProp?: string;
    isLocked?: boolean;
};

export type IPagesFilter = Pick<
    ISegmentFilterListItem,
    "tooltipKey" | "iconName" | "textKey" | "onClick" | "isTabsComponent"
> & {
    name: string;
};

export type ISegmentPageUrlResultsProps = Pick<
    ISegmentPageResultsProps,
    "rulesHighlightWords" | "rulesExactMatchHighlights" | "selectedSite" | "hideUrlsPreview"
> & {
    getUrlsPreview: (rules: IRule[]) => Promise<ISegmentUrl[]>;
    isSelectedSiteAllowed: boolean;
    showTooLongSegmentRulesErrorText: boolean;
    percentageChangeFromLimitCharacters: string;
};

export type ISegmentPageUrlListProps = Pick<
    ISegmentUrlListProps,
    "selectedSite" | "urls" | "wordHightlights" | "exactPhraseHighlights" | "shouldShowFilter"
> & {
    isFilterComponent?: boolean;
};

type PartialRecord<K extends keyof any, T> = Partial<Record<K, T>>;

// Represents the rule itself, without type field
export type IPureRule = PartialRecord<"words" | "exact" | "folders" | "exactURLS", string[]>;

export enum ESelectedDomainErrorType {
    WARNING,
    UPGRADE_ERROR,
    SENSITIVE_DATA,
    NOT_ENOUGH_DATA,
}

export enum ENewRuleType {
    INCLUDE = RuleTypes.include,
    EXCLUDE = RuleTypes.exclude,
    BOTH = 3,
    NONE,
}

export type IRuleSelectedValuesRowProps = {
    valuesList: string[];
    title: string;
    onRemoveValues: (strings: string[]) => void;
    onRuleEdit?: () => void;
    isFilterMode: boolean;
    maxChipsInRow?: number;
    maxChipWidth?: number;
    ruleType?: RuleTypes;
    filterType?: string;
    actualCurrentIndex?: number;
};

export interface ICompleteRuleChipItem extends IChipItemProps {
    actionIconName?: string;
    maxChipWidth?: number;
    isAllPagesPlaceholder?: boolean;
}

export interface ISegmentFilter {
    rule: IRule;
    onApply: (currentRule: IRule, filterType?: string, selectedTab?: number) => void;
    isMidTierUser?: boolean;
    filterType?: string;
    selectedTab?: number;
}

export interface IFoldersFilter extends ISegmentFilter {
    folderPredictionsLoading: boolean;
    folderPredictions: IFolderPredictions;
}

export interface ICustomStringsFilter extends ISegmentFilter {
    ruleIndex: number;
    selectedSite: string;
    getUrlsPreview: (rules: IRule[]) => Promise<ISegmentUrl[]>;
    hideUrlsPreview: boolean;
    hasExistingUrlsCompoundStringNoRobots: (rules: IRule[]) => Promise<boolean>;
}

export interface ISegmentModal {
    isOpen: boolean;
    onModalClose: () => void;
    onConfirm: () => void;
    isDisabledConfirmButton?: boolean;
    modalTitle: string;
    modalConfirmButtonLabel: string;
    modalText?: string;
    modalDisclaimer: string;
    modalHint?: string;
    colorDisclaimer?: boolean;
    confirmButtonType?: ButtonType;
    cancelButtonType?: ButtonType;
    modalWidth?: number;
    children?: ReactNode;
}

export type ISegmentModalWrapper = Pick<
    ISegmentModal,
    "isOpen" | "onModalClose" | "onConfirm" | "isDisabledConfirmButton"
>;

export interface ISegmentNameModal extends ISegmentModalWrapper {
    modalConfirmButtonLabel: string;
    children: ReactNode;
}

export {
    IFolderPredictions,
    IRule,
    IWordPredictions,
    IPopularSegments,
    RuleTypes,
    ISite,
    ISegmentRuleItem,
    IWordsFilterConfig,
    ISegmentsUrlParams,
    ISegmentUrl,
    ISegmentUrlListItemHighlightProps,
};
